import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';

import './back-to-top.css';

/**
 * Back to top -- one button for the whole signed-in app.
 *
 * Mounted once in MainLayout (src/jsx/index.jsx), so every routed page
 * has it and no page adds its own. The window is what scrolls on these
 * pages, so that is what is watched; a page that scrolls inside its own
 * pane (chat) never moves the window and never shows the button.
 *
 * It is the topbar's .lms-icon-btn, reused -- back-to-top.css only says
 * where it sits and how it arrives.
 */

// A screen and a half down, and never less than 600px: far enough that
// getting back is real work, not so far that the reader has given up.
const threshold = () => Math.max(600, window.innerHeight * 1.5);

const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  const frame = useRef(0);

  useEffect(() => {
    const check = () => {
      frame.current = 0;
      // Same value twice is a no-op for React, so this is cheap per frame.
      setVisible(window.scrollY > threshold());
    };

    // One read per animation frame, however fast the scroll events come.
    const schedule = () => {
      if (!frame.current) frame.current = window.requestAnimationFrame(check);
    };

    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, []);

  const goToTop = useCallback(() => {
    const reduceMotion = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // 'auto' is an instant jump: nothing in the app sets scroll-behavior
    // globally, so the smooth glide only happens when it is asked for.
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

    /* The button unmounts once the page is back at the top, and a focused
       element that disappears drops focus to <body> -- a keyboard user
       would have to start again from the address bar. Focus goes to the
       page instead, without interrupting the scroll. */
    const page = document.querySelector('.content-body');
    if (page) {
      if (!page.hasAttribute('tabindex')) page.setAttribute('tabindex', '-1');
      page.focus({ preventScroll: true });
    }
  }, []);

  if (!visible) return null;

  return (
    <button
      type="button"
      className="lms-icon-btn lms-to-top"
      onClick={goToTop}
      aria-label="Back to top"
      title="Back to top"
    >
      <ArrowUp aria-hidden="true" />
    </button>
  );
};

export default BackToTop;
